import { forwardRef, useEffect, useState } from 'react';

import { cn } from '../../utils/cn.js';

import { Input } from './input.jsx';

/**
 * A debounced input component that delays the onChange event by a specified duration.
 *
 * @param {Object} props - The input component props.
 * @param {string | number} props.value - The initial value of the input.
 * @param {function} props.onChange - The callback function to be called when the input value changes.
 * @param {number} [props.debounce=500] - The duration in milliseconds to delay the onChange event.
 * @param {...React.InputHTMLAttributes<HTMLInputElement>} props - Additional props to be passed to the input element.
 *
 * @return {JSX.Element} The debounced input component.
 */
export const DebouncedInput = forwardRef(
  (
    { value: initialValue, onChange, className, debounce = 500, ...props },
    ref,
  ) => {
    const [value, setValue] = useState(initialValue);

    useEffect(() => {
      setValue(initialValue);
    }, [initialValue]);

    useEffect(() => {
      const timeout = setTimeout(() => {
        onChange(value);
      }, debounce);

      return () => clearTimeout(timeout);
    }, [value]);

    return (
      <Input
        ref={ref}
        {...props}
        value={value}
        className={cn(className)}
        onChange={(e) => {
          setValue(e.target.value);
        }}
      />
    );
  },
);
